import React, { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ThemeProvider } from './components/theme-provider'
import { Toaster, toast } from 'sonner'
import { Footer } from './components/ui/footer'
import { CookieModal } from './components/cookie-consent/cookie-modal'
import { useCookieConsent } from './hooks/use-cookie-consent'
import { useAuth } from './hooks/use-auth'
import { debug, asDebugArea } from './lib/debug'
import { ScrollRestoration } from './components/scroll-restoration'
import { setDatabaseOfflineState } from './lib/supabase'
import { SITE_CONFIG } from './lib/constants'

// Pages
import HomePage from './pages/Home'
import ServicesPage from './pages/Services'
import AiKickstarterPage from './pages/services/AiKickstarter'
import KnowledgeEngineeringPage from './pages/services/KnowledgeEngineering'
import PartnerAIPage from './pages/services/PartnerAI'
import OperationalAssessmentPage from './pages/services/OperationalAssessment'
import AIOpportunityPage from './pages/services/AIOpportunity'
import StrategicTransformationPage from './pages/services/StrategicTransformation'
import ContactPage from './pages/Contact'
import PrivacyPage from './pages/Privacy'
import TermsPage from './pages/Terms'
import SuccessStoriesPage from './pages/SuccessStories'
import SurveyPage from './pages/Survey'
import SurveyCreatePage from './pages/survey/create'
import SurveyResultsPage from './pages/survey/results'
import SurveyManagePage from './pages/survey/manage'
import SurveyRespondPage from './pages/survey/respond'
import ThankYouPage from './pages/survey/thank-you'
import SurveyInsightsPage from './pages/survey/insights'
import UpdatePasswordPage from './pages/auth/update-password'
import AuthSuccessPage from './pages/auth/success'
import InsightsPage from './pages/Insights'
import OurApproachPage from './pages/OurApproach'
import MeetTheTeamPage from './pages/MeetTheTeam'
import Egta2025Page from './pages/downloads/Egta2025'
import EgtaBoard2025Page from './pages/downloads/EgtaBoard2025'
import Egta2026Page from './pages/downloads/Egta2026'
import FutureOfMedia2026Page from './pages/downloads/FutureOfMedia2026'

// List of paths that should show database offline notifications
const DB_DEPENDENT_PATHS = [
  '/survey/create',
  '/survey/results',
  '/survey/manage',
  '/survey/insights',
  '/auth/update-password'
];

// Only set up unhandledrejection listener for protected routes
const setupErrorHandlers = () => {
  const currentPath = window.location.pathname;
  const isProtectedRoute = DB_DEPENDENT_PATHS.some(path => 
    currentPath.startsWith(path) || 
    (currentPath.includes('/survey/') && !currentPath.includes('/thank-you'))
  );
  
  // Only set up error handlers if we're on a protected route
  if (!isProtectedRoute) {
    return;
  }
  
  // Catch unhandled promise rejections that might be coming from Supabase
  window.addEventListener('unhandledrejection', (event) => {
    // Check if it's a network error related to Supabase
    const errorMessage = event.reason?.message || '';
    if (
      (errorMessage.includes('fetch') || errorMessage.includes('network'))
      && (errorMessage.includes('failed') || errorMessage.includes('error'))
    ) {
      console.warn('Caught unhandled network error, setting DB offline:', event.reason);
      setDatabaseOfflineState(true);
      
      // Prevent the error from showing in console
      event.preventDefault();
    }
  });
  
  // Force offline mode if configured
  if (SITE_CONFIG.FORCE_DATABASE_OFFLINE) {
    setDatabaseOfflineState(true);
  }
};

export default function App() {
  const { showModal, preferences, acceptAll, savePreferences } = useCookieConsent()
  const { 
    isAuthenticated, 
    isAdmin, 
    isLoading, 
    isDatabaseOffline, 
    shouldInitializeAuth,
    initializeAuth
  } = useAuth()
  const location = useLocation()

  // Check if current path should show database offline toast
  const shouldShowOfflineToast = () => {
    const currentPath = location.pathname;
    return DB_DEPENDENT_PATHS.some(path => currentPath.startsWith(path)) ||
           currentPath.includes('/survey/') && !currentPath.includes('/thank-you');
  }

  // Set up error handlers if needed based on route
  useEffect(() => {
    setupErrorHandlers();
  }, [location.pathname]);

  // Manually trigger auth initialization when navigating to protected routes
  useEffect(() => {
    if (shouldInitializeAuth()) {
      initializeAuth();
    }
  }, [location.pathname, initializeAuth, shouldInitializeAuth]);

  // Debug initial mount - only on protected routes
  useEffect(() => {
    if (shouldInitializeAuth()) {
      debug(asDebugArea('STATE'), 'App: Initial mount')
    }
  }, [shouldInitializeAuth]) 

  // Debug auth state changes - only when in protected routes
  useEffect(() => {
    if (!isLoading && shouldInitializeAuth()) {
      debug(asDebugArea('STATE'), 'App: Auth state updated', {
        isAuthenticated,
        isAdmin,
        isLoading: false,
        isDatabaseOffline
      })
    }
  }, [isAuthenticated, isAdmin, isLoading, isDatabaseOffline, shouldInitializeAuth])

  // Handle database offline status - only show on relevant pages
  useEffect(() => {
    if (isDatabaseOffline && shouldShowOfflineToast()) {
      toast.warning(
        "Login features unavailable",
        {
          description: "The survey management and login features are temporarily unavailable. Please try again later.",
          duration: 10000, // 10 seconds
          id: "database-offline" // Prevent duplicate toasts
        }
      )
    }
  }, [isDatabaseOffline, location.pathname])

  // Add a special error handler for console errors that might be related to Supabase
  // Only set this up on protected routes
  useEffect(() => {
    // Only set up console error interceptor on protected routes
    if (!shouldShowOfflineToast()) {
      return () => {};
    }
    
    const originalConsoleError = console.error;
    
    console.error = function(...args) {
      // Check if this error might be related to Supabase
      const errorString = args.join(' ');
      if (
        errorString.includes('supabase') || 
        (errorString.includes('fetch') && errorString.includes('fail'))
      ) {
        setDatabaseOfflineState(true);
      }
      
      // Call original console.error with all arguments
      originalConsoleError.apply(console, args);
    };
    
    return () => {
      console.error = originalConsoleError;
    };
  }, [location.pathname]);
  
  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
        <ScrollRestoration />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<Navigate to="/our-approach" replace />} />
          <Route path="/our-approach" element={<OurApproachPage />} />
          <Route path="/meet-the-team" element={<MeetTheTeamPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/ai-kickstarter" element={<AiKickstarterPage />} />
          <Route path="/services/knowledge-engineering" element={<KnowledgeEngineeringPage />} />
          <Route path="/services/partner-ai" element={<PartnerAIPage />} />
          <Route path="/services/operational-assessment" element={<OperationalAssessmentPage />} />
          <Route path="/services/ai-opportunity" element={<AIOpportunityPage />} />
          <Route path="/services/strategic-transformation" element={<StrategicTransformationPage />} />
          {/* Redirect old AI Kickstarter route to new location */}
          <Route path="/ai-kickstarter" element={<Navigate to="/services/ai-kickstarter" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/downloads/egta-2025" element={<Egta2025Page />} />
          <Route path="/downloads/egta-board-2025" element={<EgtaBoard2025Page />} />
          <Route path="/downloads/egta-2026" element={<Egta2026Page />} />
          {/* Short address printed under the QR code on the egta Amsterdam 2026 slides */}
          <Route path="/egta" element={<Navigate to="/downloads/egta-2026" replace />} />
          <Route path="/downloads/future-of-media-2026" element={<FutureOfMedia2026Page />} />
          {/* Short address behind the QR code on the Future of Media London 2026 slides */}
          <Route path="/fom" element={<Navigate to="/downloads/future-of-media-2026" replace />} />
          <Route path="/survey" element={<SurveyPage />} />
          <Route path="/survey/create" element={
            isDatabaseOffline ? 
            <Navigate to="/" replace /> : 
            <SurveyCreatePage />
          } />
          <Route path="/survey/results" element={
            isDatabaseOffline ? 
            <Navigate to="/" replace /> : 
            <SurveyResultsPage />
          } />
          <Route path="/survey/manage" element={
            isDatabaseOffline ? 
            <Navigate to="/" replace /> : 
            <SurveyManagePage />
          } />
          <Route path="/survey/:code/respond" element={
            isDatabaseOffline ? 
            <Navigate to="/" replace /> : 
            <SurveyRespondPage />
          } />
          <Route path="/survey/thank-you" element={<ThankYouPage />} />
          <Route path="/survey/:code/insights" element={
            isDatabaseOffline ? 
            <Navigate to="/" replace /> : 
            <SurveyInsightsPage />
          } />
          <Route path="/auth/update-password" element={
            isDatabaseOffline ?
            <Navigate to="/" replace /> :
            <UpdatePasswordPage />
          } />
          <Route path="/auth/success" element={<AuthSuccessPage />} />
          {/* Catch all route for 404s */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Footer />
        <Toaster position="bottom-right" className="z-[100]" />
        {showModal && (
          <CookieModal
            onAcceptAll={acceptAll}
            onSavePreferences={savePreferences}
            initialPreferences={preferences}
          />
        )}
      </div>
    </ThemeProvider>
  )
}
